
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';

import {
  FieldCondition,
  FormFieldConfig,
  FormSection,
  FormWizardSectionState,
} from './models/form-wizard.models';
import { FormWizardFieldRendererComponent } from './form-wizard-field-renderer.component';

@Component({
  selector: 'app-form-wizard-section',
  standalone: true,
  imports: [TooltipModule, FormWizardFieldRendererComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './form-wizard-section.component.html',
  styleUrls: ['./form-wizard-section.component.scss'],
})
export class FormWizardSectionComponent<T = any> {
  @Input({ required: true }) section!: FormSection<T>;
  @Input({ required: true }) sectionState!: FormWizardSectionState;
  @Input({ required: true }) form!: FormGroup;
  @Input() layout: 'default' | 'modal' = 'default';

  @Output() toggle = new EventEmitter<number>();

  get sectionIndex(): number {
    return this.sectionState.index;
  }

  isSectionVisible(): boolean {
    return this.evaluateCondition(this.section.showIf);
  }

  isFieldVisible(field: FormFieldConfig<T>): boolean {
    return this.evaluateCondition(field.showIf) && this.evaluateCondition(field.dependsOn);
  }

  getFieldCssClass(field: FormFieldConfig<T>): string {
    return `col-${field.colSpan || 12}`;
  }

  requestToggle(): void {
    if (this.section.collapsible) {
      this.toggle.emit(this.sectionIndex);
    }
  }

  private evaluateCondition(condition?: FieldCondition<T>): boolean {
    if (!condition) {
      return true;
    }

    const fieldValue = this.form.get(condition.field)?.value;

    switch (condition.operator) {
      case 'equals':
        return fieldValue === condition.value;
      case 'notEquals':
        return fieldValue !== condition.value;
      case 'contains':
        return fieldValue?.includes?.(condition.value) || false;
      case 'greater':
        return fieldValue != null && fieldValue > condition.value;
      case 'less':
        return fieldValue != null && fieldValue < condition.value;
      case 'greaterOrEqual':
        return fieldValue != null && fieldValue >= condition.value;
      case 'lessOrEqual':
        return fieldValue != null && fieldValue <= condition.value;
      case 'isNotEmpty':
        return fieldValue !== null && fieldValue !== undefined && fieldValue !== '';
      case 'isEmpty':
        return fieldValue === null || fieldValue === undefined || fieldValue === '';
      default:
        return true;
    }
  }
}
